// Shared recurring-deduction helpers — the single source of the "collected X/N"
// math used by BOTH the Recurring tab (progress) and the Salary tab (installment
// rows). Master data lives in Employee.recurring[]; installments materialise as
// salary_em rows carrying recurring_uuid. Pure: no I/O.
//
// Employee.recurring[] entry shape (Xano table 8 nested):
//   { type: 'ค่าฝึกอบรม'|'เงินกู้'|'อื่นๆ', amountTotal, amount (per installment),
//     N (installments), remark, uuid, salary_em_id: int[], created_*/modified_* }

export const RECURRING_TYPES = ['ค่าฝึกอบรม', 'เงินกู้', 'อื่นๆ']

export function recurringTypeTone(type) {
  if (type === 'เงินกู้') return 'bg-violet-50 text-violet-700 ring-violet-200'
  if (type === 'ค่าฝึกอบรม') return 'bg-sky-50 text-sky-700 ring-sky-200'
  return 'bg-slate-100 text-slate-600 ring-slate-200'
}

// Collected count derived from a set of salary_em rows (authoritative — matches
// what gen2 actually wrote). Pass the salary_em rows in scope; filters by uuid.
export function collectedFromRows(entry, salaryEmRows) {
  const uuid = entry?.uuid
  if (!uuid || !Array.isArray(salaryEmRows)) return 0
  return salaryEmRows.filter((r) => r && r.recurring_uuid === uuid).length
}

// Collected count from the entry's own salary_em_id[] mirror. Fallback only —
// this mirror is NOT maintained by gen2, so it is usually empty. Prefer the
// authoritative count from /api/hr/employee-recurring-collected (see below).
export function collectedFromEntry(entry) {
  return Array.isArray(entry?.salary_em_id) ? entry.salary_em_id.length : 0
}

// uuid → { collected, amount } map from the /employee-recurring-collected rows
// (aggregated salary_em by recurring_uuid — the true installments deducted).
export function indexCollected(rows) {
  const m = new Map()
  for (const r of Array.isArray(rows) ? rows : []) {
    if (!r?.recurring_uuid) continue
    m.set(String(r.recurring_uuid), { collected: Number(r.collected) || 0, amount: Number(r.amount) || 0 })
  }
  return m
}

// Authoritative collected count for one entry: the salary_em aggregate keyed by
// uuid, falling back to the (usually empty) salary_em_id[] mirror.
export function collectedFor(map, entry) {
  const hit = map && entry?.uuid ? map.get(String(entry.uuid)) : null
  return hit ? hit.collected : collectedFromEntry(entry)
}

// Progress model for a recurring entry given a known collected count.
export function recurringProgress(entry, collected) {
  const total = Number(entry?.N || 0)
  const per = Number(entry?.amount || 0)
  const done = collected != null ? Number(collected) : collectedFromEntry(entry)
  const remaining = Math.max(0, total - done)
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0
  return {
    collected: done,
    total,
    remaining,
    amountPer: per,
    amountRemaining: remaining * per,
    amountTotal: Number(entry?.amountTotal || per * total),
    done: total > 0 && done >= total,
    pct,
  }
}

// How many EXTRA installments หักผ่อนเพิ่ม may still take from a plan. Mirrors the cap
// in Xano api 681, which mirrors gen2's two stop rules — never past N rows, and never
// past the outstanding balance (amountTotal + Σ signed amount; gen2 stops below 10 baht,
// hence the +9). Xano enforces it; this only keeps the dialog from offering what Xano
// will refuse. `hit` is the entry's {collected, amount} from indexCollected — absent
// means nothing has been deducted yet. NOT collectedFor(): its salary_em_id[] fallback
// is a different count from the rows Xano checks against.
export function extraInstallmentsLeft(entry, hit) {
  const n = Number(entry?.N || 0)
  const per = Number(entry?.amount || 0)
  if (!entry?.uuid || !entry?.type || !(n > 0) || !(per > 0)) return 0
  const collected = Number(hit?.collected || 0)
  const outstanding = Number(entry?.amountTotal || 0) + Number(hit?.amount || 0)
  if (outstanding < 10) return 0
  return Math.max(0, Math.min(n - collected, Math.floor((outstanding + 9) / per)))
}

// Blank entry factory for the "add" form. uuid is minted server-side by Xano.
export function newRecurring() {
  return { type: 'ค่าฝึกอบรม', amountTotal: 0, amount: 0, N: 0, remark: '' }
}
